/**
 * PushQuickConfig - Quick push notification configuration
 *
 * Provides title/body with FieldSourceToggle mapping support,
 * tap action selector, and shared PushDeliveryConfig.
 */

import React from "react";
import { Input } from "@/components/ui/Input";
import { FieldSourceToggle } from "../FieldSourceToggle";
import { PushDeliveryConfig } from "./PushDeliveryConfig";
import { useI18n } from "@/i18n";
import { useNotificationActions } from "@/features/notification-templates/hooks/useNotificationTemplates";
import type { BaseCustomRendererProps } from "./types";
import { configFieldKey } from "./types";

export const PushQuickConfig: React.FC<BaseCustomRendererProps> = ({
  nodeData,
  isDisabled = false,
  onBatchUpdate,
  fieldSourceHandlersFactory,
  availableSources = [],
  customInputs = [],
  contextVariables = [],
  startVariables = [],
}) => {
  const { t } = useI18n();
  const { data: actions = [] } = useNotificationActions();

  const handleUpdate = (field: string, value: unknown) => {
    if (onBatchUpdate) {
      onBatchUpdate({ [field]: value });
    }
  };

  // Create field handlers for title, body, and push_message
  const titleFieldKey = configFieldKey("title");
  const bodyFieldKey = configFieldKey("body");
  const pushMessageFieldKey = configFieldKey("push_message");
  const titleHandlers =
    fieldSourceHandlersFactory?.createFieldHandlers(titleFieldKey);
  const bodyHandlers =
    fieldSourceHandlersFactory?.createFieldHandlers(bodyFieldKey);
  const pushMessageHandlers =
    fieldSourceHandlersFactory?.createFieldHandlers(pushMessageFieldKey);

  const isSendMessageAction = (nodeData.push_action as string) === "send-message";

  return (
    <div className="space-y-4">
      {/* Title */}
      <FieldSourceToggle
        fieldKey={titleFieldKey}
        label={t("imageWorkflows.pushTarget.notificationTitle")}
        description={t("imageWorkflows.pushTarget.variableHint")}
        required={true}
        availableSources={availableSources}
        customInputs={customInputs}
        contextVariables={contextVariables}
        startVariables={startVariables}
        mapping={titleHandlers?.mapping}
        onMappingChange={titleHandlers?.onMappingChange || (() => {})}
        onKeyPathChange={titleHandlers?.onKeyPathChange || (() => {})}
        onMappingRemove={titleHandlers?.onMappingRemove || (() => {})}
        disabled={isDisabled}
        compatibleTypes={["string"]}
      >
        <Input
          value={(nodeData.title as string) || ""}
          onChange={(e) => handleUpdate("title", e.target.value || undefined)}
          placeholder={t(
            "imageWorkflows.pushTarget.notificationTitlePlaceholder",
          )}
          disabled={isDisabled}
        />
      </FieldSourceToggle>

      {/* Body */}
      <FieldSourceToggle
        fieldKey={bodyFieldKey}
        label={t("imageWorkflows.pushTarget.notificationBody")}
        description={t("imageWorkflows.pushTarget.variableHint")}
        required={true}
        availableSources={availableSources}
        customInputs={customInputs}
        contextVariables={contextVariables}
        startVariables={startVariables}
        mapping={bodyHandlers?.mapping}
        onMappingChange={bodyHandlers?.onMappingChange || (() => {})}
        onKeyPathChange={bodyHandlers?.onKeyPathChange || (() => {})}
        onMappingRemove={bodyHandlers?.onMappingRemove || (() => {})}
        disabled={isDisabled}
        compatibleTypes={["string"]}
      >
        <textarea
          value={(nodeData.body as string) || ""}
          onChange={(e) => handleUpdate("body", e.target.value || undefined)}
          rows={2}
          disabled={isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
          placeholder={t(
            "imageWorkflows.pushTarget.notificationBodyPlaceholder",
          )}
        />
      </FieldSourceToggle>

      {/* Tap Action */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("imageWorkflows.pushTarget.tapAction")}
        </label>
        <select
          value={(nodeData.push_action as string) || ""}
          onChange={(e) =>
            handleUpdate("push_action", e.target.value || undefined)
          }
          disabled={isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
        >
          <option value="">
            {t("imageWorkflows.pushTarget.tapActionNone")}
          </option>
          {actions.map((action) => (
            <option key={action.id} value={action.id}>
              {action.label}
            </option>
          ))}
        </select>
      </div>

      {/* Message Content - shown when send-message action is selected */}
      {isSendMessageAction && (
        <FieldSourceToggle
          fieldKey={pushMessageFieldKey}
          label={t("imageWorkflows.pushTarget.sendMessageContent")}
          description={t("imageWorkflows.pushTarget.sendMessageContentHint")}
          required={true}
          availableSources={availableSources}
          customInputs={customInputs}
          contextVariables={contextVariables}
          startVariables={startVariables}
          mapping={pushMessageHandlers?.mapping}
          onMappingChange={pushMessageHandlers?.onMappingChange || (() => {})}
          onKeyPathChange={pushMessageHandlers?.onKeyPathChange || (() => {})}
          onMappingRemove={pushMessageHandlers?.onMappingRemove || (() => {})}
          disabled={isDisabled}
          compatibleTypes={["string"]}
        >
          <textarea
            value={(nodeData.push_message as string) || ""}
            onChange={(e) =>
              handleUpdate("push_message", e.target.value || undefined)
            }
            rows={2}
            disabled={isDisabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
            placeholder={t(
              "imageWorkflows.pushTarget.sendMessageContentPlaceholder",
            )}
          />
        </FieldSourceToggle>
      )}

      <hr className="border-gray-200" />

      {/* Delivery Config (target, sound, badge) */}
      <PushDeliveryConfig
        nodeData={nodeData}
        isDisabled={isDisabled}
        onUpdate={handleUpdate}
      />
    </div>
  );
};

export default PushQuickConfig;
